/**
 * สัตว์เลี้ยง — คณิตของการเดินตามเจ้าของ
 *
 * **client เป็นผู้ตัดสินตำแหน่งของสัตว์เลี้ยงเอง ไม่ใช่ server**
 * ขัดกับกฎ server-authoritative ข้อ 1 ไหม — ไม่ขัด เพราะสัตว์เลี้ยงใน MVP
 * **ไม่มีผลต่อการต่อสู้เลย** ไม่ตี ไม่รับดาเมจ ไม่บังทาง ไม่ใช่เป้าให้ใครเลือก
 * ค่าพลังที่ของชิ้นนั้นให้มาจาก `equipTotals` ซึ่ง server คิดอยู่แล้วเหมือนของชิ้นอื่น
 * ตำแหน่งจึงเป็นเรื่องของการวาดล้วน ให้แต่ละเครื่องคิดเองประหยัดกว่าการ sync
 * ทุก tick ต่อผู้เล่นทุกคน (ข้อนี้คุยไว้ใน docs/HANDOVER.md)
 *
 * ถ้าวันหนึ่งสัตว์เลี้ยงช่วยตีหรือเก็บของ **ต้องย้ายไปให้ server ตัดสิน**
 * ไม่ใช่เพิ่มตรรกะต่อในไฟล์นี้
 *
 * ทุกฟังก์ชันในไฟล์นี้เป็น pure function ไม่สุ่ม ไม่อ่าน state และไม่รู้จัก Babylon
 * จึงเทสต์ได้ด้วยตัวเลขเปล่า ๆ
 */

/** จุดบนพื้น (ไม่สนความสูง สัตว์เลี้ยงที่ลอยใช้ `float` ในรูปร่างจัดการเอง) */
export interface Pt {
  x: number;
  z: number;
}

/** ระยะที่พยายามรักษาไว้ข้างหลังเจ้าของ */
export const PET_GAP = 1.7;

/**
 * ความเร็วของสัตว์เลี้ยง (หน่วยต่อวินาที)
 *
 * **เร็วกว่าผู้เล่นโดยตั้งใจ** (ผู้เล่นราว 6) ไม่งั้นวิ่งทางยาวแล้วตามไม่ทัน
 * ค่อย ๆ ห่างออกไปเรื่อย ๆ จนต้องวาร์ปตาม ซึ่งเห็นแล้วสะดุดตา
 */
export const PET_SPEED = 8;

/**
 * ห่างเกินเท่านี้ให้วาร์ปมาเลย ไม่ต้องวิ่งตาม
 *
 * เกิดได้จริงตอนเกิดใหม่ที่ตะเกียง หรือตอน server แก้ตำแหน่งให้
 * ถ้าไม่มีข้อนี้ สัตว์เลี้ยงจะวิ่งลากเส้นผ่านครึ่งแผนที่ให้คนอื่นเห็น
 */
export const PET_WARP_DIST = 14;

/**
 * เข้าใกล้จุดหมายกว่านี้ถือว่าถึงแล้ว หยุดเดิน
 *
 * ต้องมีช่องว่างเผื่อไว้ ไม่งั้นสัตว์เลี้ยงจะกระตุกไปมารอบจุดหมายตอนเจ้าของยืนเฉย
 * เพราะทุกเฟรมมันขยับเลยจุดไปเล็กน้อยแล้วหันกลับ
 */
export const PET_SLACK = 0.25;

/** ผลของการขยับหนึ่งเฟรม */
export interface PetStep {
  x: number;
  z: number;
  /** ทิศที่หันหน้า (เรเดียน ตามข้อตกลง `atan2(dirX, dirZ)` เหมือนตัวละคร) */
  ry: number;
  /** กำลังเคลื่อนที่อยู่ไหม ใช้เลือกท่าทาง */
  moving: boolean;
}

/** จุดที่สัตว์เลี้ยงควรไปยืน — ข้างหลังเจ้าของตามทิศที่เจ้าของหันอยู่ */
export function petHomePoint(owner: Pt, ownerRy: number): Pt {
  return {
    x: owner.x - Math.sin(ownerRy) * PET_GAP,
    z: owner.z - Math.cos(ownerRy) * PET_GAP,
  };
}

/**
 * ขยับสัตว์เลี้ยงไปหนึ่งเฟรม
 *
 * @param cur     ตำแหน่งปัจจุบัน
 * @param owner   ตำแหน่งเจ้าของ
 * @param ownerRy ทิศที่เจ้าของหันอยู่
 * @param dt      เวลาที่ผ่านไป (วินาที)
 * @param ry      ทิศที่หันอยู่ตอนนี้ ใช้ต่อเมื่อยืนนิ่ง (ไม่มีทิศเดินให้อ้าง)
 */
export function petStep(
  cur: Pt,
  owner: Pt,
  ownerRy: number,
  dt: number,
  ry = ownerRy,
): PetStep {
  // เฟรมที่กระโดด (สลับแท็บกลับมา) ต้องไม่ให้ขยับทีเดียวข้ามครึ่งแผนที่
  const step = PET_SPEED * Math.min(Math.max(dt, 0), 0.1);

  const home = petHomePoint(owner, ownerRy);

  // ไกลจากเจ้าของเกินไปให้วาร์ป วัดจากตัวเจ้าของไม่ใช่จากจุดหมาย
  // เพราะจุดหมายหมุนรอบตัวเจ้าของ ระยะถึงมันจึงไม่ได้บอกว่า "หลงทาง" จริง
  if (Math.hypot(owner.x - cur.x, owner.z - cur.z) > PET_WARP_DIST) {
    return { x: home.x, z: home.z, ry: ownerRy, moving: false };
  }

  const dx = home.x - cur.x;
  const dz = home.z - cur.z;
  const dist = Math.hypot(dx, dz);
  if (dist <= PET_SLACK) {
    // ถึงที่แล้ว หันหน้าทางเดียวกับเจ้าของ จะได้ดูเหมือนรออยู่ด้วยกัน
    return { x: cur.x, z: cur.z, ry: ownerRy, moving: false };
  }

  // ก้าวสุดท้ายไม่เลยจุดหมาย ไม่งั้นเกิดอาการแกว่งข้ามไปข้ามมา
  const move = Math.min(step, dist);
  return {
    x: cur.x + (dx / dist) * move,
    z: cur.z + (dz / dist) * move,
    ry: move > 0 ? Math.atan2(dx, dz) : ry,
    moving: true,
  };
}
